<template>
  <Teleport to="body">
    <div v-if="modelValue" class="bottom-sheet-wrapper">
      <!-- Overlay -->
      <Transition name="overlay">
        <div
            v-if="modelValue"
            class="overlay"
            @click="closeSheet"
        />
      </Transition>

      <!-- Bottom Sheet -->
      <Transition name="slide-up">
        <div
            v-if="modelValue"
            ref="bottomSheet"
            class="bottom-sheet"
            :style="{ maxHeight: maxHeight + 'px' }"
        >
          <!-- Header with handle bar and close button -->
          <div class="header">
            <!-- Handle bar (centered) -->
            <div class="handle-container">
              <div class="handle-bar" @click="closeSheet" />
            </div>

            <!-- Close button -->
            <button
                @click="closeSheet"
                class="close-button"
                aria-label="Close bottom sheet"
            >
              <svg
                  class="close-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
              >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Content -->
          <div class="content" :style="{ maxHeight: contentMaxHeight + 'px' }">
            <slot />
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, Teleport } from 'vue'

// Props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  maxHeightRatio: {
    type: Number,
    default: 0.9 // 90% of screen height
  },
  closeOnOverlay: {
    type: Boolean,
    default: true
  }
})

// Emits
const emit = defineEmits(['update:modelValue', 'close'])

// Refs
const bottomSheet = ref(null)
const clientHeight = ref(window.innerHeight)

// Computed
const maxHeight = computed(() => {
  return Math.floor(clientHeight.value * props.maxHeightRatio)
})

const contentMaxHeight = computed(() => {
  // Subtract header height (approximately 56px)
  return maxHeight.value - 56
})

// Methods
const closeSheet = () => {
  if (props.closeOnOverlay) {
    emit('update:modelValue', false)
    emit('close')
  }
}

const handleResize = () => {
  clientHeight.value = window.innerHeight
}

const handleEscape = (event) => {
  if (event.key === 'Escape' && props.modelValue) {
    closeSheet()
  }
}

// Lifecycle
onMounted(() => {
  window.addEventListener('resize', handleResize)
  window.addEventListener('keydown', handleEscape)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('keydown', handleEscape)

  // Clean up body styles if component is unmounted while sheet is open
  if (props.modelValue) {
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
  }
})

// Watch for body scroll prevention
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    // Store current scroll position
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
    document.body.style.overflow = 'hidden'
  } else {
    // Restore scroll position
    const scrollY = document.body.style.top
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    document.body.style.overflow = ''
    window.scrollTo(0, parseInt(scrollY || '0') * -1)
  }
})
</script>

<style scoped>
/* Bottom sheet wrapper ensures proper stacking */
.bottom-sheet-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 9998;
}

.bottom-sheet-wrapper > * {
  pointer-events: auto;
}

/* Overlay */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

/* Bottom Sheet */
.bottom-sheet {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  z-index: 9999;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
}

.handle-container {
  flex: 1;
  display: flex;
  justify-content: center;
}

.handle-bar {
  width: 40px;
  height: 4px;
  background-color: #d1d5db;
  border-radius: 9999px;
  cursor: pointer;
}

.close-button {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.close-button:hover {
  background-color: #f3f4f6;
}

.close-icon {
  width: 20px;
  height: 20px;
  color: #6b7280;
}

/* Content */
.content {
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.content::-webkit-scrollbar {
  width: 6px;
}

.content::-webkit-scrollbar-track {
  background: transparent;
}

.content::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}

/* Overlay transitions */
.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* Bottom sheet slide up transitions */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>